import type { LibrarySymbolInfo } from 'charting_library/datafeed-api'

import { Exchange } from '@/types/generated/common/types'
import type { VenueSchedule } from '@/types/generated/public_api'

export interface ChartMarketSession {
  session: string
  timezone: string
  type: string
}

const ALWAYS_OPEN_SESSION: ChartMarketSession = {
  session: '24x7',
  timezone: 'Etc/UTC',
  type: 'crypto',
}

const MARKET_SESSIONS: Partial<Record<Exchange, ChartMarketSession>> = {
  [Exchange.EXCHANGE_XNAS]: {
    session: '0930-1600',
    timezone: 'America/New_York',
    type: 'stock',
  },
  [Exchange.EXCHANGE_XNYS]: {
    session: '0930-1600',
    timezone: 'America/New_York',
    type: 'stock',
  },
  [Exchange.EXCHANGE_OPRA]: {
    session: '0930-1600',
    timezone: 'America/New_York',
    type: 'option',
  },
  [Exchange.EXCHANGE_XKRX]: {
    session: '0900-1530',
    timezone: 'Asia/Seoul',
    type: 'stock',
  },
}

/** Regular session metadata used by TradingView's native market-status indicator. */
export function getChartMarketSession(exchange: Exchange): ChartMarketSession {
  return MARKET_SESSIONS[exchange] ?? ALWAYS_OPEN_SESSION
}

export type ChartSessionCalendar = Pick<LibrarySymbolInfo, 'session_holidays' | 'corrections'>

const hhmm = (t: string) => t.replace(':', '')

/**
 * The venue's dated exceptions in TradingView's `session_holidays` /
 * `corrections` grammars, so closed days and shortened sessions are cut from
 * the time axis instead of rendering as a gap. Both lists are local dates
 * from the backend calendar. Empty for a `24x7` session: the venue has no
 * weekly session to punch holes in (GLBX's chart session is `24x7` even
 * though its calendar lists early closes).
 */
export function toTradingViewCalendar(
  session: ChartMarketSession,
  schedule: VenueSchedule | undefined
): ChartSessionCalendar {
  if (session.session === '24x7' || !schedule) return {}
  const calendar: ChartSessionCalendar = {}
  if (schedule.holidays.length > 0) {
    calendar.session_holidays = schedule.holidays.join(',')
  }
  if (schedule.sessionOverrides.length > 0) {
    calendar.corrections = schedule.sessionOverrides
      .map((o) => `${hhmm(o.open)}-${hhmm(o.close)}:${o.date}`)
      .join(';')
  }
  return calendar
}
